import { Object3D, Quaternion, Vector3 } from 'three';

import { defaultPoleTarget } from '../../Cartographer/lib/solvers/twoBoneIK';

const DEFAULT_ROOT_BONE_NAME = 'pelvis';
const MHR_ROOT_BONE_NAME = 'root';
const DEFAULT_CCD_ITERATIONS = 8;
const DEFAULT_TARGET_EPSILON = 0.0005;
const MIN_DIRECTION_LENGTH_SQUARED = 1e-8;

interface EffectorConfig {
  chainRootBoneName: string;
  twoBoneMidBoneName?: string;
  reversePoleVector?: boolean;
}

// MotionEditor works with backend-rigged characters that expose Cartwheel's
// canonical bone keys on the scene graph (`autoAssign` / bone name).
const AXEL_EFFECTOR_CONFIG_BY_BONE_NAME: Partial<Record<string, EffectorConfig>> = {
  left_wrist: {
    chainRootBoneName: 'left_shoulder',
    twoBoneMidBoneName: 'left_elbow',
    reversePoleVector: true,
  },
  right_wrist: {
    chainRootBoneName: 'right_shoulder',
    twoBoneMidBoneName: 'right_elbow',
    reversePoleVector: true,
  },
  left_ankle: {
    chainRootBoneName: 'left_hip',
    twoBoneMidBoneName: 'left_knee',
    reversePoleVector: true,
  },
  right_ankle: {
    chainRootBoneName: 'right_hip',
    twoBoneMidBoneName: 'right_knee',
    reversePoleVector: true,
  },
  head: {
    chainRootBoneName: 'neck',
  },
};

const MHR_EFFECTOR_CONFIG_BY_BONE_NAME: Partial<Record<string, EffectorConfig>> = {
  l_wrist: {
    chainRootBoneName: 'l_uparm',
    twoBoneMidBoneName: 'l_lowarm',
    reversePoleVector: true,
  },
  r_wrist: {
    chainRootBoneName: 'r_uparm',
    twoBoneMidBoneName: 'r_lowarm',
    reversePoleVector: true,
  },
  l_talocrural: {
    chainRootBoneName: 'l_upleg',
    twoBoneMidBoneName: 'l_lowleg',
    reversePoleVector: true,
  },
  r_talocrural: {
    chainRootBoneName: 'r_upleg',
    twoBoneMidBoneName: 'r_lowleg',
    reversePoleVector: true,
  },
  c_head: {
    chainRootBoneName: 'c_neck',
  },
};

export interface PoseManipulationSelection {
  handleKey: string;
  boneName: string;
}

interface PoseMachine {
  applyTarget(targetWorldPosition: Vector3): void;
}

class PoseMachineBase implements PoseMachine {
  protected readonly worldToLocalScratch = new Vector3();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applyTarget(_targetWorldPosition: Vector3): void { }

  protected setObjectWorldPosition(object: Object3D, worldPosition: Vector3) {
    const parent = object.parent;
    if (!parent) {
      object.position.copy(worldPosition);
      object.updateMatrixWorld(true);
      return;
    }

    this.worldToLocalScratch.copy(worldPosition);
    parent.worldToLocal(this.worldToLocalScratch);
    object.position.copy(this.worldToLocalScratch);
    object.updateMatrixWorld(true);
  }
}

class RootTranslationMachine extends PoseMachineBase {
  constructor(private readonly rootBone: Object3D) {
    super();
  }

  applyTarget(targetWorldPosition: Vector3): void {
    this.setObjectWorldPosition(this.rootBone, targetWorldPosition);
  }
}

class TwoBoneLimbMachine extends PoseMachineBase {
  private readonly targetEpsilonSquared: number;
  private readonly poleTargetParentLocalPosition = new Vector3();

  private readonly rootWorldPosition = new Vector3();
  private readonly midWorldPosition = new Vector3();
  private readonly effectorWorldPosition = new Vector3();
  private readonly rootToTargetDirection = new Vector3();
  private readonly currentRootToMidDirection = new Vector3();
  private readonly currentMidToEffectorDirection = new Vector3();
  private readonly desiredRootToMidDirection = new Vector3();
  private readonly desiredMidToEffectorDirection = new Vector3();
  private readonly clampedTargetWorldPosition = new Vector3();
  private readonly poleTarget = new Vector3();
  private readonly poleToRootDirection = new Vector3();
  private readonly bendAxis = new Vector3();

  private readonly rootWorldDeltaQuaternion = new Quaternion();
  private readonly midWorldDeltaQuaternion = new Quaternion();
  private readonly parentWorldQuaternion = new Quaternion();
  private readonly parentWorldQuaternionInverse = new Quaternion();
  private readonly localDeltaQuaternion = new Quaternion();

  private hasInitializedPoleTarget = false;

  constructor(
    private readonly rootBone: Object3D,
    private readonly midBone: Object3D,
    private readonly effectorBone: Object3D,
    private readonly reversePoleVector: boolean,
    targetEpsilon: number,
  ) {
    super();
    this.targetEpsilonSquared = targetEpsilon * targetEpsilon;
  }

  applyTarget(targetWorldPosition: Vector3): void {
    this.rootBone.getWorldPosition(this.rootWorldPosition);
    this.midBone.getWorldPosition(this.midWorldPosition);
    this.effectorBone.getWorldPosition(this.effectorWorldPosition);

    if (
      this.effectorWorldPosition.distanceToSquared(targetWorldPosition) <=
      this.targetEpsilonSquared
    ) {
      return;
    }

    const rootLength = this.rootWorldPosition.distanceTo(this.midWorldPosition);
    const midLength = this.midWorldPosition.distanceTo(
      this.effectorWorldPosition,
    );
    if (
      rootLength * rootLength < MIN_DIRECTION_LENGTH_SQUARED ||
      midLength * midLength < MIN_DIRECTION_LENGTH_SQUARED
    ) {
      return;
    }

    if (!this.hasInitializedPoleTarget) {
      this.initializePoleTarget(rootLength, midLength);
    }

    this.restorePoleTarget();

    this.rootToTargetDirection
      .copy(targetWorldPosition)
      .sub(this.rootWorldPosition);
    let targetDistance = this.rootToTargetDirection.length();
    if (targetDistance * targetDistance < MIN_DIRECTION_LENGTH_SQUARED) {
      this.rootToTargetDirection
        .copy(this.effectorWorldPosition)
        .sub(this.rootWorldPosition);
      targetDistance = this.rootToTargetDirection.length();
      if (targetDistance * targetDistance < MIN_DIRECTION_LENGTH_SQUARED) {
        return;
      }
    }

    this.rootToTargetDirection.normalize();

    const maxReach = rootLength + midLength - 1e-4;
    const minReach = Math.abs(rootLength - midLength) + 1e-4;
    targetDistance = Math.max(minReach, Math.min(maxReach, targetDistance));

    this.clampedTargetWorldPosition
      .copy(this.rootToTargetDirection)
      .multiplyScalar(targetDistance)
      .add(this.rootWorldPosition);

    this.currentRootToMidDirection
      .copy(this.midWorldPosition)
      .sub(this.rootWorldPosition)
      .normalize();
    this.poleToRootDirection.copy(this.poleTarget).sub(this.rootWorldPosition);
    this.bendAxis
      .crossVectors(this.rootToTargetDirection, this.poleToRootDirection)
      .normalize();
    if (this.bendAxis.lengthSq() < MIN_DIRECTION_LENGTH_SQUARED) {
      this.bendAxis
        .crossVectors(
          this.rootToTargetDirection,
          this.currentRootToMidDirection,
        )
        .normalize();
      if (this.bendAxis.lengthSq() < MIN_DIRECTION_LENGTH_SQUARED) {
        return;
      }
    }

    const cosAngleRoot = clampToUnitInterval(
      (rootLength * rootLength +
        targetDistance * targetDistance -
        midLength * midLength) /
      (2 * rootLength * targetDistance),
    );
    const rootAngle = Math.acos(cosAngleRoot);

    this.rootWorldDeltaQuaternion.setFromAxisAngle(this.bendAxis, -rootAngle);
    this.desiredRootToMidDirection
      .copy(this.rootToTargetDirection)
      .applyQuaternion(this.rootWorldDeltaQuaternion)
      .normalize();
    this.rootWorldDeltaQuaternion.setFromUnitVectors(
      this.currentRootToMidDirection,
      this.desiredRootToMidDirection,
    );
    this.applyWorldRotationDelta(this.rootBone, this.rootWorldDeltaQuaternion);

    this.rootBone.updateMatrixWorld(true);
    this.midBone.getWorldPosition(this.midWorldPosition);
    this.effectorBone.getWorldPosition(this.effectorWorldPosition);

    this.currentMidToEffectorDirection
      .copy(this.effectorWorldPosition)
      .sub(this.midWorldPosition);
    this.desiredMidToEffectorDirection
      .copy(this.clampedTargetWorldPosition)
      .sub(this.midWorldPosition);
    if (
      this.currentMidToEffectorDirection.lengthSq() <
      MIN_DIRECTION_LENGTH_SQUARED ||
      this.desiredMidToEffectorDirection.lengthSq() <
      MIN_DIRECTION_LENGTH_SQUARED
    ) {
      return;
    }

    this.currentMidToEffectorDirection.normalize();
    this.desiredMidToEffectorDirection.normalize();
    this.midWorldDeltaQuaternion.setFromUnitVectors(
      this.currentMidToEffectorDirection,
      this.desiredMidToEffectorDirection,
    );
    this.applyWorldRotationDelta(this.midBone, this.midWorldDeltaQuaternion);
    this.midBone.updateMatrixWorld(true);
  }

  private applyWorldRotationDelta(
    joint: Object3D,
    worldDeltaQuaternion: Quaternion,
  ) {
    const parent = joint.parent;
    if (parent) {
      parent.getWorldQuaternion(this.parentWorldQuaternion);
      this.parentWorldQuaternionInverse
        .copy(this.parentWorldQuaternion)
        .invert();
      this.localDeltaQuaternion
        .copy(this.parentWorldQuaternionInverse)
        .multiply(worldDeltaQuaternion)
        .multiply(this.parentWorldQuaternion);
      joint.quaternion.premultiply(this.localDeltaQuaternion).normalize();
      return;
    }

    joint.quaternion.premultiply(worldDeltaQuaternion).normalize();
  }

  private initializePoleTarget(rootLength: number, midLength: number) {
    this.poleTarget.copy(
      defaultPoleTarget(
        this.rootWorldPosition,
        this.midWorldPosition,
        this.effectorWorldPosition,
        rootLength + midLength,
      ),
    );

    if (this.reversePoleVector) {
      this.poleTarget
        .sub(this.rootWorldPosition)
        .multiplyScalar(-1)
        .add(this.rootWorldPosition);
    }

    this.poleTargetParentLocalPosition.copy(this.poleTarget);
    if (this.rootBone.parent) {
      this.rootBone.parent.worldToLocal(this.poleTargetParentLocalPosition);
    }

    this.hasInitializedPoleTarget = true;
  }

  private restorePoleTarget() {
    this.poleTarget.copy(this.poleTargetParentLocalPosition);
    if (this.rootBone.parent) {
      this.rootBone.parent.localToWorld(this.poleTarget);
    }
  }
}

class LimbMachine extends PoseMachineBase {
  private readonly joints: Object3D[];
  private readonly targetEpsilonSquared: number;

  private readonly effectorWorldPosition = new Vector3();
  private readonly jointWorldPosition = new Vector3();
  private readonly jointToEffectorDirection = new Vector3();
  private readonly jointToTargetDirection = new Vector3();

  private readonly worldDeltaQuaternion = new Quaternion();
  private readonly parentWorldQuaternion = new Quaternion();
  private readonly parentWorldQuaternionInverse = new Quaternion();
  private readonly localDeltaQuaternion = new Quaternion();

  constructor(
    private readonly effectorBone: Object3D,
    chainRootBone: Object3D | null,
    private readonly ccdIterations: number,
    targetEpsilon: number,
  ) {
    super();
    this.joints = collectCcdJoints(effectorBone, chainRootBone);
    this.targetEpsilonSquared = targetEpsilon * targetEpsilon;
  }

  applyTarget(targetWorldPosition: Vector3): void {
    if (this.joints.length === 0) {
      this.setObjectWorldPosition(this.effectorBone, targetWorldPosition);
      return;
    }

    for (let i = 0; i < this.ccdIterations; i++) {
      this.effectorBone.getWorldPosition(this.effectorWorldPosition);
      if (
        this.effectorWorldPosition.distanceToSquared(targetWorldPosition) <=
        this.targetEpsilonSquared
      ) {
        break;
      }

      for (const joint of this.joints) {
        joint.getWorldPosition(this.jointWorldPosition);
        this.effectorBone.getWorldPosition(this.effectorWorldPosition);

        this.jointToEffectorDirection
          .copy(this.effectorWorldPosition)
          .sub(this.jointWorldPosition);
        this.jointToTargetDirection
          .copy(targetWorldPosition)
          .sub(this.jointWorldPosition);

        if (
          this.jointToEffectorDirection.lengthSq() <
          MIN_DIRECTION_LENGTH_SQUARED ||
          this.jointToTargetDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQUARED
        ) {
          continue;
        }

        this.jointToEffectorDirection.normalize();
        this.jointToTargetDirection.normalize();
        this.worldDeltaQuaternion.setFromUnitVectors(
          this.jointToEffectorDirection,
          this.jointToTargetDirection,
        );

        const parent = joint.parent;
        if (parent) {
          parent.getWorldQuaternion(this.parentWorldQuaternion);
          this.parentWorldQuaternionInverse
            .copy(this.parentWorldQuaternion)
            .invert();
          this.localDeltaQuaternion
            .copy(this.parentWorldQuaternionInverse)
            .multiply(this.worldDeltaQuaternion)
            .multiply(this.parentWorldQuaternion);
          joint.quaternion.premultiply(this.localDeltaQuaternion).normalize();
        } else {
          joint.quaternion.premultiply(this.worldDeltaQuaternion).normalize();
        }

        joint.updateMatrixWorld(true);
      }
    }
  }
}

export class PoseManipulationSystem {
  private readonly ccdIterations = DEFAULT_CCD_ITERATIONS;
  private readonly targetEpsilon = DEFAULT_TARGET_EPSILON;

  private activeSelection: PoseManipulationSelection | null = null;
  private activeMachine: PoseMachine | null = null;
  private activeEffectorBone: Object3D | null = null;
  private readonly constraintMachinesByBoneName = new Map<
    string,
    PoseMachine
  >();
  private readonly targetWorldPosition = new Vector3();
  private readonly activeEffectorWorldPosition = new Vector3();
  private readonly constraintWorldPosition = new Vector3();

  constructor(private readonly scene: Object3D) { }

  dispose() {
    this.clearActiveSelection();
    this.constraintMachinesByBoneName.clear();
  }

  setActiveSelection(selection: PoseManipulationSelection): boolean {
    if (
      this.activeSelection?.handleKey === selection.handleKey &&
      this.activeSelection.boneName === selection.boneName &&
      this.activeMachine &&
      this.activeEffectorBone
    ) {
      return true;
    }

    const effectorBone = resolveBone(this.scene, selection.boneName);
    if (!effectorBone) {
      this.clearActiveSelection();
      return false;
    }

    this.activeSelection = selection;
    this.activeEffectorBone = effectorBone;
    this.activeMachine = this.createMachine(effectorBone);
    effectorBone.getWorldPosition(this.targetWorldPosition);
    return true;
  }

  clearActiveSelection() {
    this.activeSelection = null;
    this.activeMachine = null;
    this.activeEffectorBone = null;
  }

  setTargetWorldPosition(position: [number, number, number] | Vector3) {
    if (!this.activeMachine) {
      return;
    }

    if (position instanceof Vector3) {
      this.targetWorldPosition.copy(position);
      return;
    }

    this.targetWorldPosition.fromArray(position);
  }

  applyConstraint(
    boneName: string,
    position: [number, number, number] | Vector3,
  ): boolean {
    let machine = this.constraintMachinesByBoneName.get(boneName);
    if (!machine) {
      const effectorBone = resolveBone(this.scene, boneName);
      if (!effectorBone) {
        return false;
      }

      machine = this.createMachine(effectorBone);
      this.constraintMachinesByBoneName.set(boneName, machine);
    }

    if (position instanceof Vector3) {
      this.constraintWorldPosition.copy(position);
    } else {
      this.constraintWorldPosition.fromArray(position);
    }

    machine.applyTarget(this.constraintWorldPosition);
    return true;
  }

  update() {
    if (!this.activeMachine) {
      return;
    }

    this.activeMachine.applyTarget(this.targetWorldPosition);
  }

  getActiveEffectorWorldPosition(): [number, number, number] | null {
    if (!this.activeEffectorBone) {
      return null;
    }

    this.activeEffectorBone.getWorldPosition(this.activeEffectorWorldPosition);
    return [
      this.activeEffectorWorldPosition.x,
      this.activeEffectorWorldPosition.y,
      this.activeEffectorWorldPosition.z,
    ];
  }

  private createMachine(effectorBone: Object3D): PoseMachine {
    if (isRootTranslationBone(effectorBone.name)) {
      return new RootTranslationMachine(effectorBone);
    }

    const effectorConfig = getEffectorConfig(effectorBone.name);
    const chainRootBone = resolveChainRootBone(
      effectorBone,
      resolveRootBoneName(this.scene),
      effectorConfig,
    );
    const twoBoneMidBoneName = effectorConfig?.twoBoneMidBoneName;
    const twoBoneMidBone =
      twoBoneMidBoneName !== undefined
        ? findAncestorBoneByName(effectorBone, twoBoneMidBoneName)
        : null;
    const reversePoleVector = effectorConfig?.reversePoleVector ?? false;

    if (chainRootBone && twoBoneMidBone) {
      return new TwoBoneLimbMachine(
        chainRootBone,
        twoBoneMidBone,
        effectorBone,
        reversePoleVector,
        this.targetEpsilon,
      );
    }

    return new LimbMachine(
      effectorBone,
      chainRootBone,
      this.ccdIterations,
      this.targetEpsilon,
    );
  }
}

function resolveRootBoneName(scene: Object3D): string {
  if (resolveBone(scene, MHR_ROOT_BONE_NAME)) {
    return MHR_ROOT_BONE_NAME;
  }

  return DEFAULT_ROOT_BONE_NAME;
}

function isRootTranslationBone(boneName: string): boolean {
  return boneName === DEFAULT_ROOT_BONE_NAME || boneName === MHR_ROOT_BONE_NAME;
}

function resolveBone(scene: Object3D, boneName: string): Object3D | null {
  const candidate = scene.getObjectByName(boneName);
  return isBone(candidate) ? candidate : null;
}

function collectCcdJoints(
  effectorBone: Object3D,
  chainRootBone: Object3D | null,
): Object3D[] {
  const joints: Object3D[] = [];
  let current = getClosestBoneParent(effectorBone);

  while (current) {
    joints.push(current);
    if (current === chainRootBone) {
      break;
    }
    current = getClosestBoneParent(current);
  }

  return joints;
}

function getClosestBoneParent(object: Object3D): Object3D | null {
  let current: Object3D | null = object.parent;
  while (current && !isBone(current)) {
    current = (current as Object3D).parent;
  }
  return current;
}

function resolveChainRootBone(
  effectorBone: Object3D,
  fallbackRootBoneName: string,
  effectorConfig: EffectorConfig | null,
): Object3D | null {
  const configuredChainRootBoneName = effectorConfig?.chainRootBoneName;
  if (configuredChainRootBoneName) {
    const configuredChainRootBone = findAncestorBoneByName(
      effectorBone,
      configuredChainRootBoneName,
    );
    if (configuredChainRootBone) {
      return configuredChainRootBone;
    }
  }

  return findAncestorBoneByName(effectorBone, fallbackRootBoneName);
}

function getEffectorConfig(effectorBoneName: string): EffectorConfig | null {
  if (AXEL_EFFECTOR_CONFIG_BY_BONE_NAME[effectorBoneName]) {
    return AXEL_EFFECTOR_CONFIG_BY_BONE_NAME[effectorBoneName];
  }
  if (MHR_EFFECTOR_CONFIG_BY_BONE_NAME[effectorBoneName]) {
    return MHR_EFFECTOR_CONFIG_BY_BONE_NAME[effectorBoneName];
  }
  return null;
}

function findAncestorBoneByName(
  effectorBone: Object3D,
  boneName: string,
): Object3D | null {
  let current = getClosestBoneParent(effectorBone);

  while (current) {
    if (current.name === boneName) {
      return current;
    }
    current = getClosestBoneParent(current);
  }

  return null;
}

function isBone(object: Object3D | null | undefined): object is Object3D {
  if (!object) {
    return false;
  }
  const candidate = object as Object3D & { isBone?: boolean };
  return candidate.isBone === true;
}

function clampToUnitInterval(value: number): number {
  return Math.max(-1, Math.min(1, value));
}
